import React, {useState} from "react";
import {useNavigate} from "react-router-dom";
import {useGlobalContext} from "../context/GlobalState";
import {BaseInputPage} from "../components/BaseInputPage.jsx";
import {loginWithMfa, CheckOtpEnabled, SetupOtp} from "../services/Request";

// MUI Imports
import {
    TextField,
    Box,
    Button,
    InputAdornment,
    IconButton,
} from "@mui/material";
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import LoadingButton from "@mui/lab/LoadingButton";
import OtpDialog from "../components/OtpDialog";

/** * Login Page Component
 * @category Pages
 * @returns {JSX.Element} Login Page
 * @description Login page component with form to enter email and password. Calls getAuthToken function from Request service to verify user's credentials and set initial access token. Then calls refreshAccessToken to set initial refresh token. If user's credentials are valid, they will be redirected to home page. If user's credentials are invalid, user will be prompted to try again.
 */
export const Login = () => {
    const [user, setUser] = useState({
        email: "",
        password: "",
    });

    const [errorMessage, setErrorMessage] = useState("");
    const [loading, setLoading] = useState(false);
    const [showPass, setShowPass] = useState("password");
    const [otpDialogOpen, setOtpDialogOpen] = useState(false);
    const [showQrCode, setShowQrCode] = useState(false);
    const navigate = useNavigate();
    const [otpAuthUrl, setOtpAuthUrl] = useState("");
    const {setTokens, clearStorageAndCookies, setIsLoggedIn} = useGlobalContext();

    function onLoginSuccess(response) {
        setErrorMessage("");
        setOtpDialogOpen(false);
        setShowQrCode(false);
        setLoading(false);
        setTokens(response);
        navigate("");
    }

    function onLoginFailure() {
        setLoading(false);
        clearStorageAndCookies();
        setIsLoggedIn(false);
    }

    async function handleSubmit(e) {
        e.preventDefault();

        try {
            setLoading(true);
            const checkOtpResponse = await CheckOtpEnabled(user.email, user.password);
            if (checkOtpResponse.status !== 200) {
                setLoading(false);
                return setErrorMessage(checkOtpResponse?.data?.message || "An error occurred");
            }

            // first time setup
            if (checkOtpResponse.data.isOtpVerified === false) {
                const setupOtpResponse = await SetupOtp(user.email, user.password);
                if (setupOtpResponse.status === 200) {
                    setOtpAuthUrl(setupOtpResponse.data.otpAuthUrl);
                    setShowQrCode(true);
                    setOtpDialogOpen(true);
                } else {
                    setErrorMessage(setupOtpResponse?.data?.message || "An error occurred");
                    setLoading(false);
                }
            }
            // OTP is enabled, show pin dialog
            else if (checkOtpResponse.data.isOtpEnabled === true) {
                setOtpDialogOpen((checkOtpResponse.data.isOtpEnabled === true));
            }
        } catch (error) {
            onLoginFailure(error);
        }
    }

    const handleOtpSubmit = async (otp) => {
        return loginWithMfa(user.email, user.password, otp);
    };

    const handleToggle = () => {
        if (showPass === "password") {
            setShowPass("text");
        } else {
            setShowPass("password");
        }
    }

    const handleResetRequest = () => {
        navigate("/reset-password");
    }

    return (
        <BaseInputPage
            pageTitle="Login"
            titleInfo="Sign In"
        >
            <>
                <Box
                    component="form"
                    noValidate
                    onSubmit={handleSubmit}
                >
                    <TextField
                        margin="normal"
                        required
                        fullWidth
                        id="email"
                        label="Email"
                        name="email"
                        onChange={(e) =>
                            setUser({...user, email: e.target.value})
                        }
                        autoComplete="email"
                        autoFocus
                        error={errorMessage !== ""}
                    />
                    <TextField
                        margin="normal"
                        required
                        fullWidth
                        name="password"
                        label="Password"
                        value={user.password}
                        onChange={(e) =>
                            setUser({...user, password: e.target.value})
                        }
                        type={showPass}
                        id="password"
                        autoComplete="current-password"
                        helperText={errorMessage}
                        error={errorMessage !== ""}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        onClick={handleToggle}
                                        sx={{color: "primary.main"}}>
                                        {showPass === "password" ? <VisibilityOutlinedIcon/> :
                                            <VisibilityOffOutlinedIcon/>}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />
                    <LoadingButton
                        type="submit"
                        fullWidth
                        variant="contained"
                        sx={{my: 1}}
                        onClick={handleSubmit}
                        loading={loading}
                        disabled={user.email === "" || user.password === ""}
                    >
                        Sign In
                    </LoadingButton>
                </Box>
                <Button
                    variant="text"
                    fullWidth
                    onClick={handleResetRequest}
                    mt={1}
                    sx={{
                        color: "primary.main",
                        cursor: "pointer",
                        background: "none",
                        border: "none",
                    }}
                >
                    Reset Password
                </Button>
            </>
            <OtpDialog
                open={otpDialogOpen}
                showQrCode={showQrCode}
                onClose={onOtpClose}
                onSubmit={handleOtpSubmit}
                otpAuthUrl={otpAuthUrl}
            />
        </BaseInputPage>
    );

    function onOtpClose(response) {
        if(response?.status === 200){
            onLoginSuccess(response);
        }
        else{
            onLoginFailure();
        }
        return setOtpDialogOpen(false);
    }
};
